import { Bubble, Dropdown, Input, Select } from 'tea-component';
import React, { useEffect, useMemo, useState } from 'react';
import './index.scss';
import { FilterEdit } from '../filters/filter_edit';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { getMetricOptions } from '@dbl/core/dataSource';
import { FieldSelect } from '../widgets/FieldSelect';
import {deepClone, useDblIntl} from '@/pages/datalab/utils/utils';
import SvgIcon from '@/pages/home/components/SvgIcon';

export function MetricItem(props: {
  column: IColumnInfo;
  index: number;
  optionMetrics: any[];
  handle: any;
  selectedItems: string[];
  onSelectItem: (newItemName: string) => void;
  allFilters: any[];
  optionFilters: any[];
  fieldSelectClassName?: string;
  handleContent?: (option: any) => React.ReactNode;
}) {
  const { column, index, optionMetrics, handle, selectedItems, onSelectItem, allFilters, optionFilters, fieldSelectClassName = '', handleContent } = props;
  const { removeItem, notifyItems } = handle;
  const { dblFormatMessage: explorationFormatMessage, commonFormatMessage, isEn} = useDblIntl("exploration");
  const [showInput, setShowInput] = useState<boolean>(false);

  const metricOptions = useMemo(() => getMetricOptions(column.metric_method ?? 0, isEn), [column, isEn]);
  const [aggregateValue, setAggregateValue] = useState<string>();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const metricName = () => {
    let aggregateText = '';
    if (column.aggregate) {
      const aggregate = metricOptions.find((item: any) => item.value === column.aggregate)?.text;
      if (aggregate) {
        aggregateText = `·${aggregate}`;
      }
    }

    return `${isEn ? column.column_display_en : column.column_display}${aggregateText}`;
  };

  const [metricAlias, setMetricAlias] = useState<string>();

  const selectedMetricFilters = useMemo(
    () => (column.metric_filters || []).map((item) => item.column_name),
    [JSON.stringify(column.metric_filters)],
  );

  useEffect(() => {
    if (!column.aggregate) {
      column.aggregate = metricOptions?.[0]?.value;
      notifyItems();
    }
    setAggregateValue(column.aggregate);
    setMetricAlias(column.metric_alias ? column.metric_alias : metricName());
  }, [column, isEn]);

  return (
    <div className="model-port item-drag">
      <div className="context-title">
        <span className="label-num">{index}</span>
        <SvgIcon type="drag-icon" className="label-icon" />
        {/* <Icon type="drop" className={`icon_drop hoverShow`} /> */}
        {!showInput ? (
          <>
            <span className="label-text">{metricAlias}</span>
            <Bubble
              dark={true}
              arrowPointAtCenter
              placement="top"
              content={
                <div>
                  <p>{explorationFormatMessage("edit_metric_name")}</p>
                </div>
              }
            >
              <i
                className="label-icon-text"
                onClick={() => {
                  setShowInput(true);
                }}
              >
                <SvgIcon type="pencil-m" />
              </i>
            </Bubble>
            <Bubble
              dark={true}
              arrowPointAtCenter
              placement="top"
              content={
                <div>
                  <p>{commonFormatMessage("delete")}</p>
                </div>
              }
            >
              <i
                className="label-icon-close"
                onClick={() => {
                  removeItem(column);
                }}
              >
                <SvgIcon type="clonse" />
              </i>
            </Bubble>
          </>
        ) : (
          <>
            <Input
              size="m"
              className="total-input"
              value={metricAlias}
              autoFocus={true}
              onChange={(value) => {
                setMetricAlias(value);
              }}
              onPressEnter={() => {
                setShowInput(false);
                column.metric_alias = metricAlias;
                setMetricAlias(column.metric_alias ? column.metric_alias : metricName());
                notifyItems();
              }}
              onBlur={() => {
                setShowInput(false);
                column.metric_alias = metricAlias;
                setMetricAlias(column.metric_alias ? column.metric_alias : metricName());
                notifyItems();
              }}
            />
            <Bubble
              dark={true}
              arrowPointAtCenter
              placement="top"
              content={
                <div>
                  <p>{commonFormatMessage("confirm")}</p>
                </div>
              }
            >
              <i
                className="label-icon-text"
                onClick={() => {
                  setShowInput(false);
                  column.metric_alias = metricAlias;
                  setMetricAlias(column.metric_alias ? column.metric_alias : metricName());
                  notifyItems();
                }}
              >
                <SvgIcon type="right-good" />
              </i>
            </Bubble>
            <Bubble
              dark={true}
              arrowPointAtCenter
              placement="top"
              content={
                <div>
                  <p>{commonFormatMessage("cancel")}</p>
                </div>
              }
            >
              <i
                className="label-icon-close"
                onClick={() => {
                  setShowInput(false);
                  setMetricAlias(column.metric_alias ? column.metric_alias : metricName());
                }}
              >
                <SvgIcon type="clonse" />
              </i>
            </Bubble>
          </>
        )}
      </div>
      <div className="context-two">
        <div className="total-tab">
          <Dropdown
            className="dtl-tab-field-select"
            appearance="button"
            button={isEn ? column.column_display_en : column.column_display}
            clickClose={false}
          >
            <FieldSelect
              className={fieldSelectClassName ?? ''}
              handleContent={handleContent}
              fieldValue={column.column_name}
              fieldSelected={selectedItems}
              fieldOptions={optionMetrics}
              onSelectItem={onSelectItem}
            />
          </Dropdown>
          {metricOptions.length === 0 ? null : (
            <>
              <span className="total-tab-text">{explorationFormatMessage("of")}</span>
              <Select
                appearance="button"
                options={metricOptions}
                value={aggregateValue}
                onChange={(value) => {
                  column.aggregate = value;
                  setAggregateValue(column.aggregate);
                  setMetricAlias(column.metric_alias ? column.metric_alias : metricName);
                  notifyItems();
                }}
              />
            </>
          )}

          <Dropdown
            className='dbl-metric-filter-add-dropdown'
            clickClose={false}
            button={
              <Bubble
                dark={true}
                arrowPointAtCenter
                placement="top"
                content={
                  <div>
                    <p>{explorationFormatMessage("and_relation")}</p>
                  </div>
                }
              >
                <i className="label-add-icon" onClick={() => setDropdownOpen(true)}>
                  <SvgIcon type="add-change" />
                </i>
              </Bubble>
            }
            appearance="pure"
            onClose={() => {
              setDropdownOpen(false);
            }}
            open={dropdownOpen}
            defaultOpen={dropdownOpen}
          >
            <FieldSelect
              className={fieldSelectClassName ?? ''}
              handleContent={handleContent}
              fieldValue={''}
              fieldSelected={selectedMetricFilters}
              fieldOptions={optionFilters}
              onSelectItem={(fieldValue) => {
                setDropdownOpen(false);
                let newColumn = allFilters.find((item) => item.column_name === fieldValue);
                if (!newColumn) {
                  return;
                }
                newColumn = deepClone(newColumn);
                column.metric_filters = [...(column.metric_filters ?? []), newColumn];
                notifyItems();
              }}
            />
          </Dropdown>
        </div>
      </div>
      <div style={{ marginLeft: 30 }}>
        <FilterEdit
          columns={column.metric_filters ?? []}
          filterLogic={column.metric_filter_logic ?? 0}
          allFilters={allFilters}
          optionFilters={optionFilters}
          dispatchFun={(payload: any) => {
            const { columnFilters, filterLogic } = payload.payload;
            if (columnFilters) {
              column.metric_filters = [...columnFilters];
            }
            if (filterLogic) {
              column.metric_filter_logic = filterLogic;
            }
            notifyItems();
          }}
          hideAddItemBtn={true}
        />
      </div>
    </div>
  );
}
